import { getApp, getApps, initializeApp } from "firebase/app";
import { getFirestore, initializeFirestore, memoryLocalCache } from "firebase/firestore";
import { browserLocalPersistence, getAuth, setPersistence } from "firebase/auth";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";
import { firebaseConfig } from "../firebaseConfig";

// Firestore guarda lo que lee SOLO en memoria. Hasta el 19/09 las herramientas
// internas lo guardaban también en disco (IndexedDB, sin cifrar): nombres,
// teléfonos, DNI y chats quedaban en el navegador de cada persona. Lo que se
// ahorraba era poco: ANDI, WhatsApp y la lista de casos Cofidis leen del VPS, y
// con la caché en memoria una recarga cuesta unas decenas de lecturas (medido
// el 19/09: 1 en ANDI, 27 en WhatsApp, 89 en el tablón).
//
// La elección por herramienta miraba `typeof process`, que en el navegador no
// existe: en la práctica TODAS las aplicaciones, también los formularios
// públicos, usaban la caché en disco. Ahora ninguna.
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

// Lo que quedó en disco de versiones anteriores se borra al arrancar. Si una
// pestaña vieja aún lo tiene abierto, el borrado espera a que se cierre (o se
// vuelve a pedir en la próxima carga).
const borrarCopiaEnDisco = () => {
  try {
    if (typeof indexedDB === "undefined") return;
    const conocida = `firestore/[DEFAULT]/${firebaseConfig.projectId}/main`;
    const borrar = (nombre) => { try { indexedDB.deleteDatabase(nombre); } catch (_) { /* nada que hacer */ } };
    borrar(conocida);
    if (typeof indexedDB.databases === "function") {
      indexedDB.databases()
        .then((bases) => bases.forEach(({ name }) => {
          if (name && name !== conocida && name.startsWith("firestore/")) borrar(name);
        }))
        .catch(() => {});
    }
  } catch (_) {
    // Modo privado o navegador sin IndexedDB: no hay nada que borrar.
  }
};

const createFirestore = (firebaseApp) => {
  try {
    return initializeFirestore(firebaseApp, {
      localCache: memoryLocalCache(),
    });
  } catch (error) {
    const shouldFallbackToExistingInstance =
      error?.code === "failed-precondition" ||
      error?.code === "unimplemented" ||
      /already been started/i.test(error?.message || "");

    if (!shouldFallbackToExistingInstance) {
      console.warn("Falling back to default Firestore initialization:", error);
    }

    return getFirestore(firebaseApp);
  }
};

borrarCopiaEnDisco();
const db = createFirestore(app);
const auth = getAuth(app);

setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error("Error setting auth persistence:", error);
});

const storage = getStorage(app);
const functions = getFunctions(app, "us-central1");

export { app, auth, db, firebaseConfig, functions, storage };
